import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { CredentialPanelSkeleton } from "@/features/credentials/components/credential-panel-skeleton";
import { useTRPC } from "@/trpc";
import { LegacyProviderCredentials } from "@/routes/_org/org/bu/$organizationBusinessUnitId/providers/credentials/_components/legacy-provider-credentials";
import { workspaceLegacySummary } from "../../workspace-legacy-summary";
import { WorkspaceRosterEditor } from "../../workspace-roster-editor";
import type { WorkspaceProvider } from "../../workspace-types";

const AMERICA_NEW_YORK = "America/New_York";

/** Credentials of a provider on the legacy credential model. */
export function LegacyDocuments({
  provider,
  organizationBusinessUnitId,
}: {
  provider: WorkspaceProvider;
  organizationBusinessUnitId: string;
}) {
  const trpc = useTRPC();
  const details = useQuery(
    trpc.providerWorkspace.legacyDetail.queryOptions({
      organizationBusinessUnitId,
      affiliationId: provider.affiliationId,
      providerUserId: provider.providerUserId,
    }),
  );
  const registry = useQuery({
    ...trpc.providers.getCredentialRegistry.queryOptions(),
    staleTime: Infinity,
  });
  const credentialsUnavailable =
    !details.data || !registry.data || details.isError || registry.isError;
  const error = details.error?.message ?? registry.error?.message;
  const summary = workspaceLegacySummary({
    provider: details.data?.provider ?? {
      id: provider.providerUserId,
      firstName: provider.firstName,
      lastName: provider.lastName,
      role: provider.role,
      specialty: provider.specialty,
      isRevealed: false,
      credentials: [],
    },
    registry: registry.data?.registry ?? {},
  });
  return (
    <LegacyProviderCredentials
      allowedFileVersionIds={details.data?.fileVersionIds ?? []}
      provider={summary}
      organizationBusinessUnitId={organizationBusinessUnitId}
      timezoneId={AMERICA_NEW_YORK}
      credentialFallback={
        credentialsUnavailable ? (
          <div className="p-6">
            {error ? (
              <div className="space-y-3" role="alert">
                <p>{error}</p>
                <Button
                  variant="outline"
                  onClick={() => {
                    void details.refetch();
                    void registry.refetch();
                  }}
                >
                  Reload
                </Button>
              </div>
            ) : (
              <CredentialPanelSkeleton />
            )}
          </div>
        ) : undefined
      }
      workspaceActions={
        details.data?.canEditRoster ? (
          <WorkspaceRosterEditor
            provider={provider}
            organizationBusinessUnitId={organizationBusinessUnitId}
          />
        ) : undefined
      }
    />
  );
}
